import * as braze from '@braze/web-sdk'

import localStorage from 'utils/localStorage'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'

import { isPeriodMoreThanMonth, parseDate } from '../../../../../packages/manyui/src/utils/date.js'

import { EngagementServiceEvents, errorOptions } from './constants'
import {
  APIConfigError,
  AuthenticationError,
  EngagementErrorBase,
  JWTUpdateError,
  MissingInitOptionsError,
  MissingJWTError,
  MissingUserIdError,
} from './errors'
import { EngagementServiceInterface, InitOptions } from './types'

let pauseId = 1

const PREFERENCE_KEY = 'braze_web_push_preference'

const PREFERENCE_DATE_KEY = 'braze_web_push_preference_updated_at'

class BrazeEngagementService implements EngagementServiceInterface {
  #isInitialized = false

  /**
   * Used to manage multiple simultaneous invocations of `engagementService.pauseEngagement` correctly.
   */
  private engagementPauses = new Set<number>()

  private inAppMessagesSubscription: string | undefined = undefined

  private user: braze.User | undefined = undefined

  private options: InitOptions | null = null

  public init(options: InitOptions) {
    try {
      if (this.isInitialized) {
        return
      }

      this.options = options
      const { userId, token } = options
      const BrazeAppId = window.__INIT__?.['app.braze.appId']
      const BrazeSDKURL = window.__INIT__?.['app.braze.sdkUrl']
      if (!BrazeAppId || !BrazeSDKURL) {
        throw new APIConfigError()
      }
      if (typeof userId !== 'number') {
        throw new MissingUserIdError()
      }
      if (!token) {
        throw new MissingJWTError()
      }

      braze.initialize(BrazeAppId, {
        baseUrl: BrazeSDKURL,
        allowUserSuppliedJavascript: true,
        enableSdkAuthentication: true,
      })

      braze.changeUser(String(userId), token)

      this.inAppMessagesSubscription = braze.subscribeToInAppMessage(this.inAppMessagesListener)

      braze.subscribeToSdkAuthenticationFailures(this.handleSDKAuthenticationFailure)

      braze.openSession()

      this.isInitialized = true
      this.user = braze.getUser()
      analyticsService.sendEvent(EngagementServiceEvents.init.success)
    } catch (error) {
      errorTrackingService.logCritical(error, errorOptions)
      analyticsService.sendEvent(EngagementServiceEvents.init.failure)
    }
  }

  public logEvent = (eventName: string, eventProperties: object): void => {
    if (!this.isInitialized) {
      return
    }

    braze.logCustomEvent(eventName, eventProperties)
  }

  public pauseEngagement = () => {
    const pause = ++pauseId
    this.engagementPauses.add(pause)

    analyticsService.sendEvent(EngagementServiceEvents.pause.started)

    return () => {
      this.engagementPauses.delete(pause)
      analyticsService.sendEvent(EngagementServiceEvents.pause.ended)

      this.onReleaseEngagement()
    }
  }

  public dispose = () => {
    if (!this.isInitialized) {
      return
    }

    if (this.inAppMessagesSubscription) {
      braze.removeSubscription(this.inAppMessagesSubscription)
    }

    if (braze.isInitialized()) {
      braze.destroy()
    }

    this.isInitialized = false
    analyticsService.sendEvent(EngagementServiceEvents.dispose.called)
  }

  public get isPaused() {
    return this.engagementPauses.size > 0
  }

  public get isInitialized() {
    return this.#isInitialized
  }

  private set isInitialized(value: boolean) {
    this.#isInitialized = value
  }

  private onReleaseEngagement = () => {
    if (!this.isInitialized || this.isPaused) {
      // still having 1+ active pauses
      return
    }

    const deferredInAppMessage = braze.getDeferredInAppMessage()
    if (deferredInAppMessage) {
      this.inAppMessagesListener(deferredInAppMessage)
    }
  }

  private inAppMessagesListener = (inAppMessage: braze.InAppMessage | braze.ControlMessage) => {
    if (!this.isInitialized) {
      return
    }

    analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.received)

    // control group messages should always be "shown"
    // this will log an impression and not show a visible message
    if (inAppMessage instanceof braze.ControlMessage) {
      braze.showInAppMessage(inAppMessage)

      analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.shownControl)
      return
    }

    if (!this.isInitialized || this.isPaused) {
      braze.deferInAppMessage(inAppMessage)

      analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.deferred)
      return
    }

    this.showInAppMessage(inAppMessage)
  }

  private showInAppMessage(inAppMessage: braze.InAppMessage) {
    braze.showInAppMessage(inAppMessage)

    analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.shown)
  }

  private handleSDKAuthenticationFailure = async (brazeError: {
    errorCode: number
    reason?: string
    userId?: string
    signature?: string
  }) => {
    try {
      if (this.options === null) {
        throw new MissingInitOptionsError()
      }

      /** @see https://braze.com/docs/developer_guide/sdk_integration/authentication/#error-codes */
      const EXPIRED_ERROR_CODE = 22
      if (brazeError.errorCode !== EXPIRED_ERROR_CODE) {
        throw new AuthenticationError(brazeError.errorCode)
      }

      const token = await this.options.getToken()
      braze.setSdkAuthenticationSignature(token)
    } catch (error) {
      const formattedError =
        error instanceof EngagementErrorBase ? error : new JWTUpdateError(String(error))
      errorTrackingService.logError(formattedError, errorOptions)
    }
  }

  public requestPushPermission() {
    if (!this.isInitialized) {
      return
    }

    if (!braze.isPushSupported()) {
      return
    }

    if (this.isPushGranted) {
      return
    }

    try {
      analyticsService.sendEvent(EngagementServiceEvents.pushNotification.permission.requested)
      braze.requestPushPermission(
        () => {
          engagementService.setIsPushGranted(true)
        },
        () => {
          engagementService.setIsPushGranted(false)
        },
      )
    } catch (error) {
      errorTrackingService.logError(error, errorOptions)
    }
  }

  public setIsPushGranted(enabled: boolean): boolean {
    if (!this.isInitialized) {
      return false
    }

    try {
      localStorage.setItem(PREFERENCE_KEY, JSON.stringify(enabled))
      localStorage.setItem(PREFERENCE_DATE_KEY, String(Date.now()))
      analyticsService.sendEvent(
        enabled
          ? EngagementServiceEvents.pushNotification.devicePreference.enabled
          : EngagementServiceEvents.pushNotification.devicePreference.disabled,
      )

      if (enabled) {
        this.user?.setPushNotificationSubscriptionType('opted_in')
      }

      return true
    } catch (error) {
      errorTrackingService.logError(error, errorOptions)

      return false
    }
  }

  public get isPushGranted(): boolean | null {
    if (!this.isInitialized || braze.isPushBlocked()) {
      return false
    }

    try {
      const storedValue = localStorage.getItem(PREFERENCE_KEY)
      const storedUpdatedAt = localStorage.getItem(PREFERENCE_DATE_KEY)
      const parsedUpdatedAt = storedUpdatedAt ? parseDate(Number(storedUpdatedAt)) : null
      const isMonthPassed = parsedUpdatedAt
        ? isPeriodMoreThanMonth(parsedUpdatedAt, Date.now())
        : true
      const value = storedValue ? JSON.parse(storedValue) : null

      if (value == null || (value === false && isMonthPassed)) {
        return null
      }

      return value
    } catch (error) {
      errorTrackingService.logError(error, errorOptions)
    }

    return null
  }
}

export const engagementService: EngagementServiceInterface = new BrazeEngagementService()

window.engagement = engagementService
